import { appConfig } from '../../../appConfig';
import { appInsights } from '../../../appInsights';
import { getAuthToken, getAuthTokenOrDefault } from '../../common/api/auth/getAuthToken';
import getLocalApiAxiosClient from '../../common/api/clients/localApiAxiosClient';
import gastrofyApi from '../../common/api/gastrofy/gastrofyApi';
import type {
    ApiGastrofyDiscoveryRequest,
    ApiGastrofyResolveRequest,
} from '../../common/api/gastrofy/requests/gastrofyRequests';
import type { ApiGastrofyRecipe } from '../../common/api/gastrofy/responses/gastrofyResponses';
import directRecipeEcommerceApi from '../../common/api/recipe/directRecipeEcommerceApi';
import distributionAdApi from '../../common/api/recipe/distributionAdApi';
import recipeApi from '../../common/api/recipe/recipeApi';
import { createDefaultException } from '../../common/exceptions/exceptionUtils';
import { currentCartFlow } from '../../common/flow/cart/currentCartFlow';
import persistance from '../../common/persistance/persistance';
import { isNortforkRecipeFoodBox, mapToComments } from '../../common/utils/recipeUtils';
import type {
    RecipeComment,
    RecipeDiscoveryRequest,
    RecipeResolveRequest,
    ResolveRecipesRequest,
    SaveRecipeCommentRequest,
} from '../models';
import {
    mapRecipeListToApiRecipeList,
    mapToHybrisRecipesRequest,
    mapToSubstituteResponse,
} from './recipeFlowMappers';

const recipeFlow = {
    getFavouriteRecipes: async () => {
        const { token } = await getAuthToken();
        const response = await recipeApi.getFavouriteRecipes(token)();
        return response;
    },
    getRecipesById: async (recipeIds: (string | number)[]) => {
        const response = await recipeApi.getRecipesById(recipeIds);
        return response;
    },
    getRecipeById: async (recipeId: string | number) => {
        const response = await recipeApi.getRecipeById(recipeId);
        return response;
    },
    getDistributionAd: async (recipeExternalId: string) => {
        const response = await distributionAdApi.getDistributionAd(recipeExternalId);
        return response;
    },
    discoverNorthforkRecipes: async (request: RecipeDiscoveryRequest) => {
        const gastrofyDiscoveryRequest: ApiGastrofyDiscoveryRequest = {
            store_identifier: request.storeId,
            offset: request.offset,
            limit: request.limit,
            data_fields: request.dataFields?.join(','),
            extra_data: request.extraData?.join(','),
            tags: request.tags?.join(','),
            exclude: request.exclude?.join(','),
            allergies: request.allergies?.join(','),
            diets: request.diets?.join(','),
            randomize: request.randomize,
            exclude_external_recipe_ids: request.excludeRecipeIds?.join(','),
            must_have_image: request.mustHaveImage,
            is_published: request.isPublished,
            match_all_products: request.matchAllProducts,
            external_product_ids: request.productIds?.join(','),
            order_by: request.orderBy?.join(','),
            external_recipe_ids: request.externalRecipeIds?.join(','),
        };

        const discoveryResponse = await gastrofyApi.discovery(gastrofyDiscoveryRequest);
        let gastrofyRecipes = discoveryResponse.data;

        gastrofyRecipes = gastrofyRecipes.map<ApiGastrofyRecipe>((gr) => ({
            ...gr,
            isFoodBox: isNortforkRecipeFoodBox(gr),
        }));

        return gastrofyRecipes;
    },
    getRecipesByProduct: async (productIds: string[], storeId: string, limit: number) => {
        const northforkRecipes = await recipeFlow.discoverNorthforkRecipes({
            limit,
            storeId,
            mustHaveImage: true,
            isPublished: true,
            matchAllProducts: false,
            dataFields: ['external_id', 'tags'],
            productIds,
            orderBy: ['product-score'],
            extraData: ['product_score'],
        });

        const sortedRecipeIds = northforkRecipes.map((gr) => gr.external_id);
        const recipes = await recipeFlow.getRecipesById(sortedRecipeIds);

        recipes.sort(
            (a, b) =>
                sortedRecipeIds.indexOf(a.externalId.toString()) -
                sortedRecipeIds.indexOf(b.externalId.toString()),
        );

        return recipes;
    },
    getRecipesByCategories: async (tagIds: string[], storeId: string, limit: number) => {
        const northforkRecipes = await recipeFlow.discoverNorthforkRecipes({
            limit,
            storeId,
            mustHaveImage: true,
            isPublished: true,
            dataFields: ['external_id', 'tags'],
            tags: tagIds,
        });

        const sortedRecipeIds = northforkRecipes.map((gr) => gr.external_id);
        const recipes = await recipeFlow.getRecipesById(sortedRecipeIds);

        recipes.sort(
            (a, b) =>
                sortedRecipeIds.indexOf(a.externalId.toString()) -
                sortedRecipeIds.indexOf(b.externalId.toString()),
        );

        return recipes;
    },
    rateRecipe: async (recipeId: string, rating1to5: number) => {
        let token: string | null = null;
        if (appConfig.coopUserSettings.isAuthenticated) {
            const { token: authenticatedToken } = await getAuthToken();
            token = authenticatedToken;
        }

        const resp = await recipeApi.rateRecipe(token)({ recipeId, rating1to5 });
        return resp;
    },
    saveFavouriteRecipe: async (recipeId: string) => {
        const { token } = await getAuthToken();
        const resp = await recipeApi.saveFavouriteRecipe(token)(recipeId);
        return resp;
    },
    removeFavouriteRecipe: async (recipeId: string) => {
        const { token } = await getAuthToken();
        const resp = await recipeApi.removeFavouriteRecipe(token)(recipeId);
        return resp;
    },
    getRecipeUserRating: async (recipeId: number) => {
        if (appConfig.coopUserSettings.isAuthenticated) {
            const { token } = await getAuthToken();
            const response = await recipeApi.getRecipeUserRatings(token)(recipeId);
            return response.rating;
        }

        const anonymousRating = persistance.recipeRatings.get(recipeId.toString());
        return anonymousRating;
    },
    getRecipeComments: async (recipeExternalId: string): Promise<RecipeComment[]> => {
        const response = await recipeApi.getRecipeComments(recipeExternalId);
        const domainComments = mapToComments(response);

        return domainComments;
    },
    saveRecipeComment: async (
        request: SaveRecipeCommentRequest,
        forbiddenNames: string[],
        isAdmin: boolean,
    ) => {
        if (
            !isAdmin &&
            forbiddenNames.some((forbiddenName) =>
                request.authorName
                    .toLowerCase()
                    .trim()
                    .includes(forbiddenName.toLowerCase().trim()),
            )
        ) {
            return Promise.reject(createDefaultException('Vänligen ange ett annat namn'));
        }

        const { token } = await getAuthToken();
        const resp = await recipeApi.saveRecipeComment(token)(request);
        return resp;
    },
    resolveRecipes: async (request: ResolveRecipesRequest, storeId: string) => {
        const cartId = await currentCartFlow.getCurrentCartId();
        if (!cartId) {
            const error = Error('No cart id');
            appInsights.trackException({ exception: error }, { place: 'resolveRecipes' });
            throw error;
        }
        const { token, shoppingUserId } = await getAuthTokenOrDefault();
        const hybrisRequest = mapToHybrisRecipesRequest(request);
        const resp = await directRecipeEcommerceApi.resolveRecipes(token)(hybrisRequest, {
            storeId,
            userId: shoppingUserId,
            cartId,
        });
        const response = mapRecipeListToApiRecipeList(resp);
        return response;
    },
    getSubstitutes: async (storeId: string, substituteIdentifier: string) => {
        const { token } = await getAuthTokenOrDefault();
        const resp = await directRecipeEcommerceApi.getSubstitutes(token)(
            storeId,
            substituteIdentifier,
        );
        const response = mapToSubstituteResponse(resp);
        return response.recipeProductItems;
    },

    resolveGastrofyRecipes: async (request: RecipeResolveRequest) => {
        const gastrofyResolveRequest: ApiGastrofyResolveRequest = {
            store: { identifier: request.storeId },
            options: request.options,
            recipes: request.recipes.map((recipe) => ({
                external_id: recipe.externalId,
                portions: recipe.portions,
            })),
        };

        const resolveResponse = await gastrofyApi.resolve(gastrofyResolveRequest);
        return resolveResponse;
    },

    getRecipeCommentsAdmin: async (query: string, page: number, pageSize: number) => {
        const client = getLocalApiAxiosClient();
        const res = await client.get<{
            comments: {
                id: number;
                recipeId: number;
                recipeExternalId: number | null;
                recipeName: string | null;
                created: string;
                author: string;
                message: string;
            }[];
            currentPageNumber: number;
            totalCount: number;
        }>('/episerver/recipeCommentAdmin', {
            params: {
                query,
                page,
                pageSize,
            },
        });

        return res.data;
    },

    deleteRecipeCommentAdmin: async (recipeId: number, commentId: number) => {
        const client = getLocalApiAxiosClient();
        await client.delete(`/episerver/recipeCommentAdmin`, {
            params: {
                recipeId,
                commentId,
            },
        });

        return true;
    },

    filterRecipesAdminTool: async (filterString: string, excludeList: string[]) => {
        const { token } = await getAuthToken();
        const resp = await recipeApi.filterRecipesAdminTool(token, filterString, excludeList);
        return resp;
    },
};

export default recipeFlow;
